import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Globe2, Lock, UserPlus2, Loader2, Mail, X, Users, AlertCircle, Check,
  Link2, Copy, ExternalLink, Eye, Search, Star, MessageCircle, Send,
  Linkedin, Twitter,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Channel share-row used inside the bilateral section to push the invite
 * link out via WhatsApp / Email / X / LinkedIn / Telegram or any external
 * link (the browser-native Web Share API on mobile).
 */
function ChannelShareRow({ url, projectTitle }) {
  if (!url) return null;
  const text = `Hey, ich lade dich ein, am Projekt "${projectTitle || ""}" auf Project Radar AR mitzuwirken:`;
  const encUrl = encodeURIComponent(url);
  const encText = encodeURIComponent(text);
  const channels = [
    {
      key: "whatsapp",
      label: "WhatsApp",
      color: "#25D366",
      href: `https://wa.me/?text=${encText}%20${encUrl}`,
      icon: MessageCircle,
    },
    {
      key: "email",
      label: "E-Mail",
      color: "#FFB800",
      href: `mailto:?subject=${encodeURIComponent("Einladung: " + (projectTitle || "Project Radar AR"))}&body=${encText}%20${encUrl}`,
      icon: Mail,
    },
    {
      key: "linkedin",
      label: "LinkedIn",
      color: "#0A66C2",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encUrl}`,
      icon: Linkedin,
    },
    {
      key: "twitter",
      label: "X / Twitter",
      color: "#FFFFFF",
      href: `https://twitter.com/intent/tweet?text=${encText}&url=${encUrl}`,
      icon: Twitter,
    },
    {
      key: "telegram",
      label: "Telegram",
      color: "#26A5E4",
      href: `https://t.me/share/url?url=${encUrl}&text=${encText}`,
      icon: Send,
    },
  ];

  const tryNativeShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: projectTitle || "Project Radar AR", text, url });
      }
    } catch (_e) { /* user cancelled */ }
  };
  const supportsNativeShare = typeof navigator !== "undefined" && !!navigator.share;

  return (
    <div data-testid="channel-share-row">
      <p className="text-[10px] uppercase tracking-widest text-[#606070] font-['JetBrains_Mono'] mb-2">
        Über einen Kanal teilen
      </p>
      <div className="flex flex-wrap gap-2">
        {channels.map((c) => {
          const Icon = c.icon;
          return (
            <a
              key={c.key}
              data-testid={`share-channel-${c.key}`}
              href={c.href}
              target="_blank"
              rel="noopener noreferrer"
              title={c.label}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-white/5 border border-white/10 text-[11px] font-['Outfit'] font-semibold text-white hover:bg-white/10 transition-colors"
              style={{ color: c.color }}
            >
              <Icon className="w-3.5 h-3.5" />
              {c.label}
            </a>
          );
        })}
        {supportsNativeShare && (
          <button
            data-testid="share-channel-native"
            onClick={tryNativeShare}
            title="Mehr Optionen…"
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-white/5 border border-white/10 text-[11px] font-['Outfit'] font-semibold text-white hover:bg-white/10 transition-colors"
          >
            <Star className="w-3.5 h-3.5" />
            Mehr…
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * VisibilityPanel — owner-only widget with three visibility modes:
 *   1. Privat (default)            — only the owner sees the project
 *   2. Bearbeitung freigeben       — bilateral: invite freelancers by email
 *                                    OR via self-service invite link, plus
 *                                    optional read-only Teaser-Share-Link
 *   3. Öffentlich auf Marktplatz   — anyone can see + apply
 *
 * Mode is derived from the project state: explicit selection updates the
 * project (publish/unpublish + invite/share toggles).
 */
export default function VisibilityPanel({ project, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [invited, setInvited] = useState([]);
  const [loadingInvites, setLoadingInvites] = useState(false);
  const [shareToken, setShareToken] = useState(project.shareToken || null);
  const [shareBusy, setShareBusy] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [inviteToken, setInviteToken] = useState(project.inviteToken || null);
  const [inviteLinkBusy, setInviteLinkBusy] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);
  // Explicit mode override — when the user clicks "Bearbeitung freigeben"
  // without yet having any invitees/token, we still need to expand that
  // section. `null` means "derive from state".
  const [selectedMode, setSelectedMode] = useState(null);
  // Freelancer directory state
  const [showPicker, setShowPicker] = useState(false);
  const [freelancers, setFreelancers] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  const [invitingId, setInvitingId] = useState(null);

  const isPublic = !!project.isPublic;
  const projectId = project.id;
  const shareUrl = shareToken ? `${window.location.origin}/share/${shareToken}` : "";
  const inviteUrl = inviteToken ? `${window.location.origin}/invite/${inviteToken}` : "";

  // Sync state when project prop changes
  useEffect(() => {
    setShareToken(project.shareToken || null);
    setInviteToken(project.inviteToken || null);
  }, [project.shareToken, project.inviteToken]);

  const loadInvites = useCallback(async () => {
    setLoadingInvites(true);
    try {
      const res = await axios.get(`${API}/projects/${projectId}/invitations`, {
        withCredentials: true,
      });
      setInvited(res.data || []);
    } catch (_e) {
      setInvited([]);
    } finally {
      setLoadingInvites(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadInvites();
  }, [loadInvites]);

  // Determine current mode — explicit selection wins over derived state
  const hasBilateral = !!(invited.length > 0 || inviteToken);
  const derivedMode = isPublic ? "public" : hasBilateral ? "bilateral" : "private";
  const currentMode = selectedMode || derivedMode;

  // ---------- Mode switchers ----------
  const setMode = async (target) => {
    if (target === currentMode) return;
    setSelectedMode(target);
    setBusy(true);
    setError("");
    try {
      if (target === "public") {
        if (!isPublic) {
          await axios.post(
            `${API}/projects/${projectId}/publish`,
            { isPublic: true },
            { withCredentials: true },
          );
        }
      } else if (target === "private") {
        // Reset everything bilateral + public
        if (isPublic) {
          await axios.post(
            `${API}/projects/${projectId}/publish`,
            { isPublic: false },
            { withCredentials: true },
          );
        }
        if (inviteToken) {
          await axios.post(
            `${API}/projects/${projectId}/invite-link/disable`,
            {},
            { withCredentials: true },
          );
          setInviteToken(null);
        }
        // Remove existing invitees (one-by-one)
        for (const u of invited) {
          await axios.delete(`${API}/projects/${projectId}/invite/${u.user_id}`, {
            withCredentials: true,
          });
        }
        await loadInvites();
        setShowPicker(false);
      } else if (target === "bilateral") {
        // Switch off public; bilateral section now expands and offers
        // freelancer-directory + email + self-service link.
        if (isPublic) {
          await axios.post(
            `${API}/projects/${projectId}/publish`,
            { isPublic: false },
            { withCredentials: true },
          );
        }
      }
      onUpdated?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Mode konnte nicht gewechselt werden.");
    } finally {
      setBusy(false);
    }
  };

  // ---------- Freelancer directory picker ----------
  const loadFreelancers = useCallback(async (q) => {
    setPickerLoading(true);
    try {
      const res = await axios.get(`${API}/freelancers`, {
        params: q ? { search: q } : {},
        withCredentials: true,
      });
      setFreelancers(res.data || []);
    } catch (_e) {
      setFreelancers([]);
    } finally {
      setPickerLoading(false);
    }
  }, []);

  const togglePicker = async () => {
    const next = !showPicker;
    setShowPicker(next);
    if (next && freelancers.length === 0) {
      await loadFreelancers("");
    }
  };

  const inviteFreelancerById = async (freelancer) => {
    setInvitingId(freelancer.user_id);
    setError("");
    try {
      await axios.post(
        `${API}/projects/${projectId}/invite`,
        { user_id: freelancer.user_id },
        { withCredentials: true },
      );
      await loadInvites();
      onUpdated?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Einladung fehlgeschlagen.");
    } finally {
      setInvitingId(null);
    }
  };

  const isAlreadyInvited = (uid) => invited.some((u) => u.user_id === uid);

  // ---------- Invite by email ----------
  const sendInvite = async (e) => {
    e?.preventDefault();
    if (!inviteEmail.trim()) return;
    setBusy(true);
    setError("");
    try {
      await axios.post(
        `${API}/projects/${projectId}/invite`,
        { email: inviteEmail.trim() },
        { withCredentials: true },
      );
      setInviteEmail("");
      await loadInvites();
      onUpdated?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Einladung fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const removeInvite = async (uid) => {
    setBusy(true);
    try {
      await axios.delete(`${API}/projects/${projectId}/invite/${uid}`, {
        withCredentials: true,
      });
      await loadInvites();
      onUpdated?.();
    } catch (_e) {
      // ignore
    } finally {
      setBusy(false);
    }
  };

  // ---------- Invite Link (self-service) ----------
  const enableInviteLink = async () => {
    setInviteLinkBusy(true);
    setError("");
    try {
      const res = await axios.post(
        `${API}/projects/${projectId}/invite-link/enable`,
        {},
        { withCredentials: true },
      );
      setInviteToken(res.data.inviteToken);
      onUpdated?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Einladungs-Link konnte nicht erstellt werden.");
    } finally {
      setInviteLinkBusy(false);
    }
  };

  const disableInviteLink = async () => {
    setInviteLinkBusy(true);
    try {
      await axios.post(
        `${API}/projects/${projectId}/invite-link/disable`,
        {},
        { withCredentials: true },
      );
      setInviteToken(null);
      onUpdated?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Einladungs-Link konnte nicht widerrufen werden.");
    } finally {
      setInviteLinkBusy(false);
    }
  };

  const copyInviteUrl = async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 1800);
    } catch (_e) { /* ignore */ }
  };

  // ---------- Teaser Share Link ----------
  const enableShare = async () => {
    setShareBusy(true);
    setError("");
    try {
      const res = await axios.post(
        `${API}/projects/${projectId}/share/enable`,
        {},
        { withCredentials: true },
      );
      setShareToken(res.data.shareToken);
      onUpdated?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Teaser-Link konnte nicht erstellt werden.");
    } finally {
      setShareBusy(false);
    }
  };

  const disableShare = async () => {
    setShareBusy(true);
    try {
      await axios.post(
        `${API}/projects/${projectId}/share/disable`,
        {},
        { withCredentials: true },
      );
      setShareToken(null);
      onUpdated?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Teaser-Link konnte nicht widerrufen werden.");
    } finally {
      setShareBusy(false);
    }
  };

  const copyShareUrl = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 1800);
    } catch (_e) { /* ignore */ }
  };

  // ---------- Mode definitions for the radio cards ----------
  const MODES = [
    {
      key: "private",
      icon: Lock,
      title: "Privat",
      subtitle: "Nur du siehst dieses Projekt",
      color: "#909098",
    },
    {
      key: "bilateral",
      icon: Users,
      title: "Bearbeitung freigeben",
      subtitle: "Eingeladene Auftragnehmer können mitwirken",
      color: "#FFB800",
    },
    {
      key: "public",
      icon: Globe2,
      title: "Öffentlich",
      subtitle: "Auf dem Marktplatz für alle sichtbar",
      color: "#00E5A0",
    },
  ];

  return (
    <div
      data-testid="visibility-panel"
      className="glass-surface rounded-xl p-5 border border-white/8"
    >
      <div className="flex items-center gap-2 mb-3">
        <Eye className="w-4 h-4 text-[#00F0FF]" />
        <h3 className="font-['Outfit'] text-base font-semibold text-white">
          Sichtbarkeit & Zusammenarbeit
        </h3>
      </div>

      {/* 3-mode selector */}
      <div data-testid="visibility-mode-selector" className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
        {MODES.map((m) => {
          const Active = currentMode === m.key;
          const ModeIcon = m.icon;
          return (
            <button
              key={m.key}
              data-testid={`mode-${m.key}-btn`}
              onClick={() => setMode(m.key)}
              disabled={busy}
              className={`text-left p-3 rounded-lg border transition-all ${
                Active
                  ? "bg-white/[0.04]"
                  : "bg-white/[0.02] border-white/10 hover:border-white/20 hover:bg-white/[0.04]"
              }`}
              style={Active ? { borderColor: `${m.color}60`, background: `${m.color}10` } : {}}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <div
                  className="w-7 h-7 rounded-md flex items-center justify-center"
                  style={{ background: `${m.color}1F`, border: `1px solid ${m.color}40` }}
                >
                  <ModeIcon className="w-3.5 h-3.5" style={{ color: m.color }} />
                </div>
                <span
                  className="font-['Outfit'] text-sm font-semibold"
                  style={{ color: Active ? m.color : "#fff" }}
                >
                  {m.title}
                </span>
                {Active && <Check className="w-3.5 h-3.5 ml-auto" style={{ color: m.color }} />}
              </div>
              <p className="text-[11px] text-[#909098] leading-snug">{m.subtitle}</p>
            </button>
          );
        })}
      </div>

      {busy && (
        <div className="flex items-center gap-2 text-[11px] text-[#909098] mb-3">
          <Loader2 className="w-3 h-3 animate-spin" /> Wechsle Modus…
        </div>
      )}

      {/* ---------- Bilateral details ---------- */}
      {currentMode === "bilateral" && (
        <div data-testid="bilateral-section" className="border-t border-white/5 pt-4 space-y-5">
          {/* Freelancer Directory Picker */}
          <div>
            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
              <div className="flex items-center gap-2">
                <Users className="w-3.5 h-3.5 text-[#FFB800]" />
                <span className="text-[11px] font-['JetBrains_Mono'] tracking-widest uppercase text-[#FFB800]">
                  Auftragnehmer-Verzeichnis
                </span>
              </div>
              <button
                data-testid="toggle-picker-btn"
                onClick={togglePicker}
                className="text-[11px] text-[#FFB800] hover:text-white transition-colors font-['Outfit'] inline-flex items-center gap-1"
              >
                {showPicker ? "Schließen" : "Verzeichnis öffnen"}
              </button>
            </div>
            <p className="text-[12px] text-[#909098] leading-relaxed mb-3">
              Wähle einen registrierten Auftragnehmer direkt aus dem Verzeichnis aus. Profil mit Skills,
              Stundensatz und Bio sichtbar — ein Klick zum Einladen.
            </p>

            {showPicker && (
              <div data-testid="freelancer-picker" className="rounded-lg border border-white/8 bg-[#060610]/60 p-3 mb-2">
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#606070]" />
                  <input
                    data-testid="picker-search-input"
                    type="text"
                    value={pickerSearch}
                    onChange={(e) => setPickerSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        loadFreelancers(pickerSearch);
                      }
                    }}
                    placeholder="Suche nach Name, Skill oder Bio…"
                    className="w-full bg-[#060610] border border-white/10 rounded-lg pl-8 pr-3 py-2 text-sm text-white placeholder:text-[#606070] focus:outline-none focus:border-[#FFB800]/50 font-['Inter']"
                  />
                </div>
                {pickerLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="w-4 h-4 animate-spin text-[#FFB800]" />
                  </div>
                ) : freelancers.length === 0 ? (
                  <div className="py-6 text-center text-[11px] text-[#606070] italic">
                    Keine Auftragnehmer gefunden.
                  </div>
                ) : (
                  <ul className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                    {freelancers.map((f) => {
                      const already = isAlreadyInvited(f.user_id);
                      const initial = (f.name || f.email || "?").trim()[0]?.toUpperCase() || "?";
                      return (
                        <li
                          key={f.user_id}
                          data-testid={`picker-row-${f.user_id}`}
                          className="flex items-center gap-3 px-3 py-2 rounded-md bg-white/[0.03] border border-white/8 hover:border-[#FFB800]/30 transition-colors"
                        >
                          <div className="w-8 h-8 rounded-full bg-[#FFB800]/15 border border-[#FFB800]/40 text-[#FFB800] flex items-center justify-center text-xs font-bold flex-shrink-0">
                            {f.picture ? (
                              <img
                                src={f.picture}
                                alt=""
                                className="w-full h-full object-cover rounded-full"
                              />
                            ) : (
                              initial
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-white font-medium truncate">
                                {f.name || f.email?.split("@")[0]}
                              </span>
                              {f.hourly_rate > 0 && (
                                <span className="text-[10px] font-['JetBrains_Mono'] text-[#FFB800]">
                                  · {f.hourly_rate}€/h
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                              {(f.skills || []).slice(0, 4).map((s) => (
                                <span
                                  key={s}
                                  className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-[#A0A0B0] font-['JetBrains_Mono']"
                                >
                                  {s}
                                </span>
                              ))}
                              {(f.skills || []).length === 0 && f.bio && (
                                <span className="text-[10px] text-[#606070] truncate max-w-xs">
                                  {f.bio.slice(0, 80)}
                                </span>
                              )}
                            </div>
                          </div>
                          <button
                            data-testid={`picker-invite-${f.user_id}`}
                            onClick={() => inviteFreelancerById(f)}
                            disabled={already || invitingId === f.user_id}
                            className={`px-3 py-1.5 rounded-md text-[11px] font-['Outfit'] font-semibold transition-all border inline-flex items-center gap-1 ${
                              already
                                ? "bg-[#00E5A0]/10 border-[#00E5A0]/30 text-[#00E5A0] cursor-default"
                                : "bg-[#FFB800]/15 border-[#FFB800]/40 text-[#FFB800] hover:bg-[#FFB800]/25"
                            } disabled:opacity-60`}
                          >
                            {invitingId === f.user_id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : already ? (
                              <Check className="w-3 h-3" />
                            ) : (
                              <UserPlus2 className="w-3 h-3" />
                            )}
                            {already ? "Eingeladen" : "Einladen"}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}

            {/* Already invited mini-list (always visible) */}
            {!loadingInvites && invited.length > 0 && (
              <ul data-testid="invites-list" className="space-y-1.5 mt-2">
                {invited.map((u) => (
                  <li
                    key={u.user_id}
                    className="flex items-center justify-between gap-2 px-3 py-1.5 rounded-md bg-white/[0.03] border border-white/8"
                  >
                    <span className="flex items-center gap-2 text-[12px] text-white truncate">
                      <Check className="w-3 h-3 text-[#00E5A0] flex-shrink-0" />
                      <span className="font-medium">{u.name || u.email?.split("@")[0]}</span>
                      <span className="text-[#606070]">·</span>
                      <span className="text-[#909098] truncate">{u.email}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => removeInvite(u.user_id)}
                      className="text-[#909098] hover:text-[#FF3B30] transition-colors p-1"
                      title="Einladung entfernen"
                      aria-label="Einladung entfernen"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Direct invite by email */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Mail className="w-3.5 h-3.5 text-[#FFB800]" />
              <span className="text-[11px] font-['JetBrains_Mono'] tracking-widest uppercase text-[#FFB800]">
                Direkte Einladung per E-Mail
              </span>
            </div>
            <p className="text-[12px] text-[#909098] leading-relaxed mb-3">
              Du kennst die E-Mail-Adresse? Direkt eintragen — der Auftragnehmer bekommt sofort Zugriff.
            </p>
            <form onSubmit={sendInvite} className="flex items-center gap-2">
              <div className="flex-1 relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#606070]" />
                <input
                  data-testid="invite-email-input"
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="freelancer@beispiel.com"
                  className="w-full bg-[#060610] border border-white/10 rounded-lg pl-8 pr-3 py-2 text-sm text-white placeholder:text-[#606070] focus:outline-none focus:border-[#FFB800]/50 font-['Inter']"
                />
              </div>
              <button
                data-testid="invite-submit-btn"
                type="submit"
                disabled={busy || !inviteEmail.trim()}
                className="px-4 py-2 rounded-lg bg-[#FFB800]/15 border border-[#FFB800]/40 text-[#FFB800] text-sm font-['Outfit'] font-semibold hover:bg-[#FFB800]/25 disabled:opacity-50 transition-all"
              >
                Einladen
              </button>
            </form>
          </div>

          {/* Self-service invite link */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Link2 className="w-3.5 h-3.5 text-[#FFB800]" />
              <span className="text-[11px] font-['JetBrains_Mono'] tracking-widest uppercase text-[#FFB800]">
                Einladungs-Link teilen (Self-Service)
              </span>
            </div>
            <p className="text-[12px] text-[#909098] leading-relaxed mb-3">
              Erstelle einen Link, den du per WhatsApp, E-Mail, LinkedIn oder beliebigem Kanal an einen
              Auftragnehmer schicken kannst. Nach Login wird er automatisch zum Projekt hinzugefügt — auch
              wenn er noch kein Konto hat.
            </p>
            {!inviteToken ? (
              <button
                data-testid="enable-invite-link-btn"
                onClick={enableInviteLink}
                disabled={inviteLinkBusy}
                className="inline-flex items-center gap-2 text-xs sm:text-sm px-4 py-2 rounded-lg font-['Outfit'] font-semibold transition-all border bg-[#FFB800]/10 border-[#FFB800]/40 text-[#FFB800] hover:bg-[#FFB800]/20 disabled:opacity-50"
              >
                {inviteLinkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                Einladungs-Link erstellen
              </button>
            ) : (
              <div className="space-y-3">
                <div className="flex items-stretch gap-2 flex-wrap">
                  <input
                    data-testid="invite-url-input"
                    readOnly
                    value={inviteUrl}
                    onFocus={(e) => e.target.select()}
                    className="flex-1 min-w-[260px] bg-[#060610] border border-[#FFB800]/30 rounded-lg px-3 py-2 text-[12px] text-white font-['JetBrains_Mono'] focus:outline-none"
                  />
                  <button
                    data-testid="copy-invite-url-btn"
                    onClick={copyInviteUrl}
                    className="px-3 py-2 rounded-lg bg-[#FFB800]/15 border border-[#FFB800]/40 text-[#FFB800] text-xs font-['Outfit'] font-semibold hover:bg-[#FFB800]/25 inline-flex items-center gap-1.5"
                    title="Link kopieren"
                  >
                    {inviteCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {inviteCopied ? "Kopiert" : "Kopieren"}
                  </button>
                </div>

                {/* Channel share buttons */}
                <ChannelShareRow url={inviteUrl} projectTitle={project.projectTitle} />

                <button
                  data-testid="disable-invite-link-btn"
                  onClick={disableInviteLink}
                  disabled={inviteLinkBusy}
                  className="text-[11px] text-[#909098] hover:text-[#FF3B30] transition-colors font-['Outfit'] inline-flex items-center gap-1"
                >
                  {inviteLinkBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
                  Link widerrufen
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---------- Public Marketplace details ---------- */}
      {currentMode === "public" && (
        <div data-testid="public-section" className="border-t border-white/5 pt-4">
          <div className="flex items-center gap-2 mb-2">
            <Globe2 className="w-3.5 h-3.5 text-[#00E5A0]" />
            <span className="text-[11px] font-['JetBrains_Mono'] tracking-widest uppercase text-[#00E5A0]">
              Auf dem Marktplatz
            </span>
          </div>
          <p className="text-[12px] text-[#909098] leading-relaxed">
            Dein Projekt ist öffentlich. Jeder Auftragnehmer kann es sehen, sich bewerben und Vorschläge
            machen. Du kannst Bewerbungen unten annehmen oder ablehnen.
          </p>
        </div>
      )}

      {/* ---------- Private mode details ---------- */}
      {currentMode === "private" && (
        <div data-testid="private-section" className="border-t border-white/5 pt-4">
          <p className="text-[12px] text-[#909098] leading-relaxed">
            Nur du kannst dieses Projekt sehen. Wechsle zu <strong className="text-white">Bearbeitung freigeben</strong>,
            um einen Auftragnehmer einzuladen, oder zu <strong className="text-white">Öffentlich</strong>, um es auf den Marktplatz zu stellen.
          </p>
        </div>
      )}

      {/* ---------- Teaser Share-Link (always available, regardless of mode) ---------- */}
      <div className="border-t border-white/5 pt-4 mt-4">
        <div className="flex items-center gap-2 mb-2">
          <Eye className="w-3.5 h-3.5 text-[#00F0FF]" />
          <span className="text-[11px] font-['JetBrains_Mono'] tracking-widest uppercase text-[#00F0FF]">
            Teaser-Share-Link (Read-Only)
          </span>
        </div>
        <p className="text-[12px] text-[#909098] leading-relaxed mb-3">
          Öffentlicher Read-Only-Link mit 3D-Vorschau und Executive Summary — ideal,
          um Stakeholdern den Scope ohne Login zu zeigen. Sprache wählbar.
        </p>

        {!shareToken ? (
          <button
            data-testid="enable-share-btn"
            onClick={enableShare}
            disabled={shareBusy}
            className="inline-flex items-center gap-2 text-xs sm:text-sm px-4 py-2 rounded-lg font-['Outfit'] font-semibold transition-all border bg-[#00F0FF]/10 border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/20 disabled:opacity-50"
          >
            {shareBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
            Teaser-Link erstellen
          </button>
        ) : (
          <div className="space-y-2">
            <div className="flex items-stretch gap-2">
              <input
                data-testid="share-url-input"
                readOnly
                value={shareUrl}
                onFocus={(e) => e.target.select()}
                className="flex-1 bg-[#060610] border border-[#00F0FF]/25 rounded-lg px-3 py-2 text-[12px] text-white font-['JetBrains_Mono'] focus:outline-none"
              />
              <button
                data-testid="copy-share-url-btn"
                onClick={copyShareUrl}
                className="px-3 py-2 rounded-lg bg-[#00F0FF]/15 border border-[#00F0FF]/40 text-[#00F0FF] text-xs font-['Outfit'] font-semibold hover:bg-[#00F0FF]/25 inline-flex items-center gap-1.5"
                title="Link kopieren"
              >
                {shareCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {shareCopied ? "Kopiert" : "Kopieren"}
              </button>
              <a
                data-testid="open-share-url-btn"
                href={shareUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-2 rounded-lg bg-white/5 border border-white/15 text-white text-xs font-['Outfit'] font-semibold hover:bg-white/10 inline-flex items-center gap-1.5"
                title="In neuem Tab öffnen"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Öffnen
              </a>
            </div>
            <button
              data-testid="disable-share-btn"
              onClick={disableShare}
              disabled={shareBusy}
              className="text-[11px] text-[#909098] hover:text-[#FF3B30] transition-colors font-['Outfit'] inline-flex items-center gap-1"
            >
              {shareBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
              Link widerrufen
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-3 flex items-start gap-2 p-3 rounded-md bg-[#FF3B30]/10 border border-[#FF3B30]/30">
          <AlertCircle className="w-3.5 h-3.5 text-[#FF3B30] flex-shrink-0 mt-0.5" />
          <span className="text-[12px] text-[#FECACA]">{error}</span>
        </div>
      )}
    </div>
  );
}
